import { inject, Injectable } from '@angular/core';
import { AccountService } from './services/account/account.service';
import { AuthorizationManagementService } from './services/authorization-management/authorization-management.service';
import { ContactManagementService } from './services/contact-management/contact-management.service';
import { KeyExchangeService } from './services/key-exchange/key-exchange.service';
import { LoginLogoutService } from './services/login-logout/login-logout.service';
import { MediaService } from './services/media/media.service';
import { MessageService } from './services/message/message.service';
import { OrgAdminFhirSharedService } from './services/org-admin-fhir/org-admin-fhir-shared.service';
import { PhantomCleanupService } from './services/phantom-cleanup/phantom-cleanup.service';
import { RoomService } from './services/room/room.service';
import { SearchInFhirDirectoryService } from './services/search-in-fhir-directory/search-in-fhir-directory.service';
import { SearchOnHomeserverService } from './services/search-on-homeserver/search-on-homeserver.service';

/**
 * Die Testtreiber-Bridge stellt die Business-Logik des Clients (ausgewählte Services) für den Testtreiber bereit.
 * Der Testtreiber kann damit als interner Testtreiber agieren.
 * Umgesetze AFOs:
 * A_25363 - Testtreiber-Modul
 */
@Injectable({ providedIn: 'root' })
export class TestDriverBridge {
  private readonly accountService = inject(AccountService);
  private readonly authorizationManagementService = inject(AuthorizationManagementService);
  private readonly contactManagementService = inject(ContactManagementService);
  private readonly keyExchangeService = inject(KeyExchangeService);
  private readonly loginLogoutService = inject(LoginLogoutService);
  private readonly mediaService = inject(MediaService);
  private readonly messageService = inject(MessageService);
  private readonly orgAdminFhirSharedService = inject(OrgAdminFhirSharedService);
  private readonly phantomCleanupService = inject(PhantomCleanupService);
  private readonly roomService = inject(RoomService);
  private readonly searchInFhirDirectoryService = inject(SearchInFhirDirectoryService);
  private readonly searchOnHomeserverService = inject(SearchOnHomeserverService);

  /**
   * Exponiert die injizierten Services auf `globalThis`, damit sie im Browser-Kontext
   * per `window.<service>` bzw. über `page.evaluate()` aus E2E-Tests und Testtreiber
   * erreichbar sind. Wird aus {@link AppComponent.ngOnInit} aufgerufen,
   * sofern `isTesttreiberEnabled === 'true'` in der App-Config gesetzt ist.
   */
  initializeServices(): void {
    console.log('Stelle Services für Testtreiber/Playwright auf globalThis bereit.');

    globalThis.accountService = this.accountService;
    globalThis.authorizationManagementService = this.authorizationManagementService;
    globalThis.contactManagementService = this.contactManagementService;
    globalThis.keyExchangeService = this.keyExchangeService;
    globalThis.loginLogoutService = this.loginLogoutService;
    globalThis.mediaService = this.mediaService;
    globalThis.messageService = this.messageService;
    globalThis.orgAdminFhirSharedService = this.orgAdminFhirSharedService;
    globalThis.phantomCleanupService = this.phantomCleanupService;
    globalThis.roomService = this.roomService;
    globalThis.searchInFhirDirectoryService = this.searchInFhirDirectoryService;
    globalThis.searchOnHomeserverService = this.searchOnHomeserverService;

    // Komfort-Hooks am App-Root registrieren, damit sie auf allen Routen verfügbar sind
    // (z.B. /rooms) und nicht nur, solange die Keybackup-Seite gerendert ist.
    globalThis.cleanupUser = (): Promise<number> => {
      // Mehrfaches Starten verhindern
      if (globalThis.__cleanupUserPromise) {
        return globalThis.__cleanupUserPromise;
      }
      const promise = this.phantomCleanupService.cleanupUser().finally(() => {
        globalThis.__cleanupUserPromise = undefined;
      });
      globalThis.__cleanupUserPromise = promise;
      return promise;
    };

    globalThis.forcePresenceOffline = (): Promise<void> => this.phantomCleanupService.forcePresenceOffline();
  }
}
